import axiosInstance from '@/app/api/axiosInstance';
import {
  StockTransferOrder,
  StockTransferOrderCreate,
  StockTransferOrderUpdate,
  StockTransferOrderListItem,
  StockTransferStatus,
  StockAvailability
} from '@/app/types/stockTransfer';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Helper to parse numeric fields (like Decimal from backend)
const parseStockTransferResponse = (transfer: StockTransferOrder): StockTransferOrder => ({
  ...transfer,
  items: transfer.items.map(item => ({
    ...item,
    quantity: item.quantity ? String(item.quantity) : '0',
    rate: item.rate ? String(item.rate) : '0',
    taxable_amount: item.taxable_amount ? String(item.taxable_amount) : '0',
    tax_amount: item.tax_amount ? String(item.tax_amount) : '0',
    total_amount: item.total_amount ? String(item.total_amount) : '0',
    notes: item.notes || '',
    // Preserve the nested item object if it exists
    item: item.item || undefined,
  })),
});

// Helper to parse list item numeric fields
const parseStockTransferListItem = (item: any): StockTransferOrderListItem => {
  // Calculate total value from items if available
  const calculatedTotal = item.items?.reduce((sum: number, transferItem: any) => {
    const quantity = parseFloat(transferItem.quantity || '0');
    const rate = parseFloat(transferItem.rate || '0');
    return sum + (quantity * rate);
  }, 0) || 0;

  return {
    ...item,
    total_value: String(calculatedTotal),
    items_count: item.items?.length || 0,
  };
};

// List stock transfer orders with pagination
export const listStockTransferOrders = async (
  params: ListQueryParams,
): Promise<PaginatedResponse<StockTransferOrderListItem>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<any>>('/stock-transfer-orders/', { params });
    response.data.items = response.data.items.map(parseStockTransferListItem);
    return response.data;
  } catch (error) {
    console.error('Failed to list stock transfer orders:', error);
    throw error;
  }
};

// Get single stock transfer order by ID
export const getStockTransferOrder = async (id: number): Promise<StockTransferOrder> => {
  try {
    const response = await axiosInstance.get<StockTransferOrder>(`/stock-transfer-orders/${id}`);
    console.log('[stockTransferService] Raw API response:', response.data);
    return parseStockTransferResponse(response.data);
  } catch (error) {
    console.error(`Failed to get stock transfer order with ID ${id}:`, error);
    throw error;
  }
};

// Create new stock transfer order
export const createStockTransferOrder = async (data: StockTransferOrderCreate): Promise<StockTransferOrder> => {
  try {
    // Ensure numeric fields are sent correctly (as strings if backend expects Decimal)
    const payload = {
      ...data,
      status: data.status || 'draft', // Default to draft status
      items: data.items.map(item => ({
        ...item,
        quantity: String(item.quantity),
        rate: String(item.rate),
        notes: item.notes || '',
      })),
    };
    
    const response = await axiosInstance.post<StockTransferOrder>('/stock-transfer-orders/', payload);
    return parseStockTransferResponse(response.data);
  } catch (error) {
    console.error('Failed to create stock transfer order:', error);
    throw error;
  }
};

// Update existing stock transfer order (only allowed in draft status)
export const updateStockTransferOrder = async (
  id: number, 
  data: StockTransferOrderUpdate
): Promise<StockTransferOrder> => {
  console.log(`updateStockTransferOrder called with id: ${id}`, data);
  try {
    // Ensure numeric fields are sent correctly
    const payload = {
      ...data,
      ...(data.items && {
        items: data.items.map(item => ({
          ...item,
          quantity: String(item.quantity),
          rate: String(item.rate),
          notes: item.notes || '',
        })),
      }),
    };
    
    console.log(`Making API request to update stock transfer order ${id}...`);
    const response = await axiosInstance.patch<StockTransferOrder>(`/stock-transfer-orders/${id}`, payload);
    console.log(`Update API request successful, response:`, response.data);
    return parseStockTransferResponse(response.data);
  } catch (error) {
    console.error(`Failed to update stock transfer order with ID ${id}:`, error);
    throw error;
  }
};

// Update stock transfer order status
export const updateStockTransferOrderStatus = async (
  id: number,
  newStatus: StockTransferStatus,
  notes?: string
): Promise<StockTransferOrder> => {
  try {
    const params = new URLSearchParams();
    params.append('new_status', newStatus);
    if (notes) {
      params.append('notes', notes);
    }
    
    const response = await axiosInstance.patch<StockTransferOrder>(
      `/stock-transfer-orders/${id}/status?${params.toString()}`
    );
    return parseStockTransferResponse(response.data);
  } catch (error) {
    console.error(`Failed to update status for stock transfer order ${id}:`, error);
    throw error;
  }
};

// Delete stock transfer order (only allowed in draft status)
export const deleteStockTransferOrder = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/stock-transfer-orders/${id}`);
  } catch (error) {
    console.error(`Failed to delete stock transfer order with ID ${id}:`, error);
    throw error;
  }
};

// Check stock availability for an item in a godown
export const checkStockAvailability = async (
  itemId: number,
  godownId: number
): Promise<StockAvailability> => {
  try {
    const response = await axiosInstance.get<any>(`/current-stock/item/${itemId}/godown/${godownId}`);
    
    // Parse the response to match our StockAvailability type
    return {
      item_id: itemId,
      godown_id: godownId,
      quantity: parseFloat(response.data.quantity || '0'),
      unit_id: response.data.unit_id,
      last_updated: response.data.last_updated,
    };
  } catch (error) {
    console.error(`Failed to check stock for item ${itemId} in godown ${godownId}:`, error);
    // Return zero stock on error
    return {
      item_id: itemId,
      godown_id: godownId,
      quantity: 0,
      unit_id: 0,
    };
  }
};

// Bulk check stock availability for multiple items
export const bulkCheckStockAvailability = async (
  items: { item_id: number; godown_id: number }[]
): Promise<StockAvailability[]> => {
  try {
    const promises = items.map(({ item_id, godown_id }) => 
      checkStockAvailability(item_id, godown_id)
    );
    return await Promise.all(promises);
  } catch (error) {
    console.error('Failed to bulk check stock availability:', error);
    return [];
  }
};